/**
 * Brand utility functions — shared across brand components.
 */

// ─── SVG Component Parser ─────────────────────────────────────
export function parseSvgComponents(svgContent) {
  if (!svgContent?.startsWith('<svg')) return [];
  const components = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  if (!svg) return [];

  // Collect text properties
  const textEls = svg.querySelectorAll('text, tspan');
  const seenText = new Set();
  textEls.forEach(el => {
    const text = el.textContent?.trim();
    if (!text || seenText.has(text)) return;
    seenText.add(text);

    const font = el.getAttribute('font-family') || el.closest('[font-family]')?.getAttribute('font-family') || 'system default';
    const size = el.getAttribute('font-size') || el.closest('[font-size]')?.getAttribute('font-size') || '?';
    const weight = el.getAttribute('font-weight') || el.closest('[font-weight]')?.getAttribute('font-weight') || 'normal';
    const fill = el.getAttribute('fill') || el.closest('[fill]')?.getAttribute('fill') || '#000';

    components.push({ type: 'text-family', label: `Font Family: ${font}`, detail: `for "${text}"`, color: '', keep: true });
    components.push({ type: 'text-size', label: `Font Size: ${size}`, detail: `for "${text}"`, color: '', keep: true });
    components.push({ type: 'text-weight', label: `Font Weight: ${weight}`, detail: `for "${text}"`, color: '', keep: true });
    components.push({ type: 'text-color', label: `Font Color`, detail: `for "${text}"`, color: fill !== 'none' ? fill : '', keep: true });
  });

  // Collect shapes
  const shapeEls = svg.querySelectorAll('circle, ellipse, rect, polygon');
  const seenShapes = new Set();
  shapeEls.forEach(el => {
    const tag = el.tagName.toLowerCase();
    const fill = el.getAttribute('fill');
    const stroke = el.getAttribute('stroke');
    const color = fill && fill !== 'none' ? fill : stroke || '';
    const label = tag === 'circle' ? `Circle (r=${el.getAttribute('r') || '?'})`
      : tag === 'ellipse' ? `Ellipse`
      : tag === 'rect' ? (el.getAttribute('rx') ? 'Rounded Rectangle' : 'Rectangle') + ` (${el.getAttribute('width')}x${el.getAttribute('height')})`
      : 'Polygon';
    const key = `${label}:${color}`;
    if (seenShapes.has(key)) return;
    seenShapes.add(key);
    components.push({ type: 'shape', label, detail: '', color, keep: true });
  });

  // Collect lines and paths
  const lineEls = svg.querySelectorAll('line, path');
  const seenLines = new Set();
  lineEls.forEach(el => {
    const tag = el.tagName.toLowerCase();
    const fill = el.getAttribute('fill');
    const stroke = el.getAttribute('stroke');
    const color = (fill && fill !== 'none') ? fill : (stroke && stroke !== 'none') ? stroke : '';
    if (!color) return; // skip invisible paths
    const d = el.getAttribute('d') || '';
    // Heuristic: short paths with only lines are "line/stroke" elements
    const isSimpleLine = tag === 'line' || (d.length < 100 && /^M[\d.\s,\-]+[LlHhVv]/.test(d));
    const label = isSimpleLine ? 'Line / Stroke' : 'Shape / Path';
    const key = `${label}:${color}`;
    if (seenLines.has(key)) return;
    seenLines.add(key);
    components.push({ type: isSimpleLine ? 'line' : 'shape', label, detail: '', color, keep: true });
  });

  return components;
}

// ─── Color Helpers ────────────────────────────────────────────
export function isDarkColor(hex) {
  if (!hex?.startsWith('#')) return false;
  const c = hex.replace('#', '');
  if (c.length < 6) return false;
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  // Luminance check — dark if below threshold
  return (r * 0.299 + g * 0.587 + b * 0.114) < 100;
}

export function invertSvgForDarkBg(innerSvg) {
  // Replace dark fills/strokes with white for dark backgrounds
  let inverted = innerSvg;
  inverted = inverted.replace(/fill=["']([^"']+)["']/g, (match, color) => {
    if (color === 'none' || color === 'transparent') return match;
    if (isDarkColor(color)) return `fill="#FFFFFF"`;
    return match;
  });
  inverted = inverted.replace(/stroke=["']([^"']+)["']/g, (match, color) => {
    if (color === 'none' || color === 'transparent') return match;
    if (isDarkColor(color)) return `stroke="#FFFFFF"`;
    return match;
  });
  return inverted;
}

// Generate shades of a hex color (5 tints: 2 lighter, original, 2 darker)
export function generateShades(hex) {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  function mix(c, t, amt) { return Math.round(c + (t - c) * amt); }
  function toHex(r, g, b) { return '#' + [r, g, b].map(n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('').toUpperCase(); }
  return [
    toHex(mix(r, 255, 0.7), mix(g, 255, 0.7), mix(b, 255, 0.7)), // lightest
    toHex(mix(r, 255, 0.35), mix(g, 255, 0.35), mix(b, 255, 0.35)), // light
    hex.toUpperCase(), // original
    toHex(mix(r, 0, 0.25), mix(g, 0, 0.25), mix(b, 0, 0.25)), // dark
    toHex(mix(r, 0, 0.5), mix(g, 0, 0.5), mix(b, 0, 0.5)), // darkest
  ];
}

// ─── Logo Variant Generator ──────────────────────────────────
export function generateLogoVariants(svgContent, brandColors) {
  if (!svgContent?.startsWith('<svg')) return [];
  const variants = [];
  const dark = brandColors?.text || '#1F2937';
  const primary = brandColors?.primary || '#3B82F6';

  // Parse viewBox
  const vbMatch = svgContent.match(/viewBox=["']([^"']+)["']/);
  const vb = vbMatch ? vbMatch[1] : '0 0 200 80';
  const [, , vw, vh] = vb.split(/[\s,]+/).map(Number);

  // Strip width/height from inner SVG for embedding
  const inner = svgContent.replace(/<svg[^>]*>/, '').replace(/<\/svg>/, '');
  const innerWhite = invertSvgForDarkBg(inner);
  const svgAttrs = svgContent.match(/<svg([^>]*)>/)?.[1] || '';

  // 1. Light background (original)
  variants.push({ name: 'Light Background', key: 'light', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vw + 40} ${vh + 40}"><rect width="${vw + 40}" height="${vh + 40}" fill="#FFFFFF"/><g transform="translate(20,20)">${inner}</g></svg>` });

  // 2. Dark background (white text/shapes)
  variants.push({ name: 'Dark Background', key: 'dark', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vw + 40} ${vh + 40}"><rect width="${vw + 40}" height="${vh + 40}" fill="${dark}"/><g transform="translate(20,20)">${innerWhite}</g></svg>` });

  // 3. Brand color background (white text/shapes)
  variants.push({ name: 'Brand Background', key: 'brand-bg', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vw + 40} ${vh + 40}"><rect width="${vw + 40}" height="${vh + 40}" fill="${primary}"/><g transform="translate(20,20)">${innerWhite}</g></svg>` });

  // 4. Square format (centered)
  const sq = Math.max(vw, vh) + 60;
  variants.push({ name: 'Square', key: 'square', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${sq} ${sq}"><rect width="${sq}" height="${sq}" fill="#FFFFFF"/><g transform="translate(${(sq - vw) / 2},${(sq - vh) / 2})">${inner}</g></svg>` });

  // 5. Wide/horizontal format (3:1)
  const wideW = Math.max(vw * 2, vh * 3);
  const wideH = wideW / 3;
  variants.push({ name: 'Wide / Horizontal', key: 'wide', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${wideW} ${wideH}"><rect width="${wideW}" height="${wideH}" fill="#FFFFFF"/><g transform="translate(${(wideW - vw) / 2},${(wideH - vh) / 2})">${inner}</g></svg>` });

  // 6. Transparent (original colors, no background)
  variants.push({ name: 'Transparent', key: 'transparent', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" ${svgAttrs.replace(/xmlns=['"][^'"]*['"]/g, '')}>${inner}</svg>` });

  // 7. White on Transparent (all fills/strokes white, no background)
  const innerAllWhite = inner.replace(/fill=["'](?!none|transparent)[^"']+["']/g, 'fill="#FFFFFF"').replace(/stroke=["'](?!none|transparent)[^"']+["']/g, 'stroke="#FFFFFF"');
  variants.push({ name: 'White on Transparent', key: 'white-transparent', svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" ${svgAttrs.replace(/xmlns=['"][^'"]*['"]/g, '')}>${innerAllWhite}</svg>` });

  return variants;
}

// ─── Font List ────────────────────────────────────────────────
export const FONT_LIST = [
  // Sans-serif
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Raleway', 'Nunito',
  'Work Sans', 'DM Sans', 'Space Grotesk', 'Outfit', 'Sora', 'Manrope', 'Plus Jakarta Sans',
  'IBM Plex Sans', 'Source Sans 3', 'Fira Sans', 'Barlow', 'Archivo', 'Cabin', 'Karla',
  'Quicksand', 'Rubik', 'Josefin Sans', 'Mukta', 'Exo 2', 'Overpass', 'Lexend', 'Urbanist',
  'Red Hat Display', 'Albert Sans', 'Figtree', 'Geist', 'Bricolage Grotesque',
  'Noto Sans', 'Mulish', 'Nunito Sans', 'Titillium Web', 'Varela Round', 'Signika',
  'Comfortaa', 'Catamaran', 'Jost', 'Asap', 'Commissioner', 'Readex Pro',
  'Be Vietnam Pro', 'Onest', 'General Sans', 'Satoshi',
  // Serif
  'Playfair Display', 'Merriweather', 'Lora', 'Libre Baskerville', 'Cormorant Garamond',
  'EB Garamond', 'Crimson Text', 'Source Serif 4', 'IBM Plex Serif', 'Bitter',
  'Vollkorn', 'DM Serif Display', 'Fraunces', 'Young Serif', 'Instrument Serif',
  'Noto Serif', 'PT Serif', 'Domine', 'Spectral', 'Brygada 1918',
  // Display
  'Syne', 'Unbounded', 'Orbitron', 'Righteous', 'Bungee', 'Abril Fatface',
  'Bebas Neue', 'Anton', 'Oswald', 'Fjalla One', 'Teko', 'Russo One',
  'Passion One', 'Black Ops One', 'Permanent Marker', 'Lobster',
  'Pacifico', 'Bangers', 'Protest Riot', 'Lilita One',
  // Monospace
  'JetBrains Mono', 'Fira Code', 'Source Code Pro', 'IBM Plex Mono', 'Space Mono',
  'Roboto Mono', 'Ubuntu Mono', 'Inconsolata', 'DM Mono',
  // Handwriting
  'Caveat', 'Dancing Script', 'Kalam', 'Patrick Hand', 'Indie Flower',
];

const _loadedFonts = new Set();
export function loadFontBatch(fonts) {
  const toLoad = fonts.filter(f => !_loadedFonts.has(f));
  if (toLoad.length === 0) return;
  toLoad.forEach(f => _loadedFonts.add(f));
  const families = toLoad.map(f => `family=${encodeURIComponent(f)}:wght@400;700`).join('&');
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  document.head.appendChild(link);
}
